/**
 * Pure dashboard aggregations (T2.03). Everything here is unit-tested; the
 * views only lay the results out. The survival math is the protocol's
 * (worksheet section 2), never re-derived here.
 */
import {
  countConditions,
  cumulativeSurvivalPct,
  diameterFromGirthCm,
  survivalRate,
  type Condition,
  type GirthRow,
  type InterventionRow,
  type MeasurementRow,
  type SpeciesRow,
  type SurveyRow,
  type TreeFlagsRow,
  type TreeLatestRow,
  type TreeRow,
  type ZoneRow,
  type ZoneSurvivalRow,
} from '@treetrack/protocol';

export type MeasurementWithGirths = MeasurementRow & { measurement_girths?: GirthRow[] | null };

export function median(values: readonly number[]): number | null {
  const v = values.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

export function girthAt(m: MeasurementWithGirths, heightCm: 10 | 130): number | null {
  const g = (m.measurement_girths ?? []).find((x) => Number(x.height_cm) === heightCm);
  return g ? Number(g.girth_cm) : null;
}

export function diameterAt(m: MeasurementWithGirths, heightCm: 10 | 130): number | null {
  const g = girthAt(m, heightCm);
  return g == null ? null : Math.round(diameterFromGirthCm(g) * 100) / 100;
}

export interface SpeciesSurvival {
  species_id: string | null;
  species: SpeciesRow | null;
  planted: number;
  measured: number;
  alive: number;
  notFound: number;
  ratePct: number | null;
  cumulativePct: number | null;
}

/** Survival by species from each tree's latest measurement. Trees never measured count as planted only. */
export function survivalBySpecies(latest: readonly TreeLatestRow[], species: readonly SpeciesRow[]): SpeciesSurvival[] {
  const byId = new Map(species.map((s) => [s.id, s]));
  const groups = new Map<string | null, Condition[]>();
  const planted = new Map<string | null, number>();
  for (const l of latest) {
    planted.set(l.species_id, (planted.get(l.species_id) ?? 0) + 1);
    if (!groups.has(l.species_id)) groups.set(l.species_id, []);
    if (l.condition) groups.get(l.species_id)!.push(l.condition);
  }
  return [...planted.keys()]
    .map((id) => {
      const s = survivalRate(countConditions(groups.get(id) ?? []));
      const p = planted.get(id) ?? 0;
      return {
        species_id: id,
        species: id ? (byId.get(id) ?? null) : null,
        planted: p,
        measured: s.found,
        alive: s.alive,
        notFound: s.notFound,
        ratePct: s.survivalRatePct,
        cumulativePct: cumulativeSurvivalPct(p, s.alive),
      };
    })
    .sort((a, b) => b.planted - a.planted);
}

/** The most recent survey row per zone (by date, then survey id for a stable tie-break). */
export function latestZoneSurvival(rows: readonly ZoneSurvivalRow[]): ZoneSurvivalRow[] {
  const best = new Map<string | null, ZoneSurvivalRow>();
  for (const r of rows) {
    const cur = best.get(r.zone_id);
    if (!cur || r.date > cur.date || (r.date === cur.date && r.survey_id > cur.survey_id)) best.set(r.zone_id, r);
  }
  return [...best.values()].sort((a, b) => (a.zone_label ?? '').localeCompare(b.zone_label ?? ''));
}

export interface GrowthPoint {
  date: string;
  n: number;
  medianHeightCm: number | null;
  medianDiameterCm: number | null;
}

export interface SpeciesGrowth {
  species_id: string | null;
  species: SpeciesRow | null;
  points: GrowthPoint[];
}

/** Median height and basal diameter per species per survey date, from living trees (H, S) only. */
export function growthBySpecies(
  measurements: readonly MeasurementWithGirths[],
  trees: readonly TreeRow[],
  surveys: readonly SurveyRow[],
  species: readonly SpeciesRow[],
): SpeciesGrowth[] {
  const treeSpecies = new Map(trees.map((t) => [t.id, t.species_id]));
  const surveyDate = new Map(surveys.map((s) => [s.id, s.date]));
  const byId = new Map(species.map((s) => [s.id, s]));
  const buckets = new Map<string | null, Map<string, { h: number[]; d: number[] }>>();
  for (const m of measurements) {
    if (m.condition !== 'H' && m.condition !== 'S') continue;
    const sp = treeSpecies.get(m.tree_id) ?? null;
    const date = surveyDate.get(m.survey_id);
    if (!date) continue;
    if (!buckets.has(sp)) buckets.set(sp, new Map());
    const perDate = buckets.get(sp)!;
    if (!perDate.has(date)) perDate.set(date, { h: [], d: [] });
    const b = perDate.get(date)!;
    if (m.height_cm != null) b.h.push(Number(m.height_cm));
    const d = diameterAt(m, 10);
    if (d != null) b.d.push(d);
  }
  return [...buckets.entries()]
    .map(([sp, perDate]) => ({
      species_id: sp,
      species: sp ? (byId.get(sp) ?? null) : null,
      points: [...perDate.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, b]) => ({ date, n: Math.max(b.h.length, b.d.length), medianHeightCm: median(b.h), medianDiameterCm: median(b.d) })),
    }))
    .sort((a, b) => b.points.reduce((s, p) => s + p.n, 0) - a.points.reduce((s, p) => s + p.n, 0));
}

export interface InterventionRollup {
  zone_id: string | null;
  zone_label: string;
  month: string; // YYYY-MM
  counts: Record<string, number>;
  wateredLitres: number;
  total: number;
}

/** Interventions per zone per month: counts by type and watered litres (T2.02's roll-up). */
export function interventionRollup(interventions: readonly InterventionRow[], trees: readonly TreeRow[], zones: readonly ZoneRow[]): InterventionRollup[] {
  const treeZone = new Map(trees.map((t) => [t.id, t.zone_id]));
  const zoneLabel = new Map(zones.map((z) => [z.id, z.label]));
  const out = new Map<string, InterventionRollup>();
  for (const i of interventions) {
    const zone_id = i.zone_id ?? (i.tree_id ? (treeZone.get(i.tree_id) ?? null) : null);
    const month = i.done_at.slice(0, 7);
    const key = `${zone_id ?? ''}|${month}`;
    if (!out.has(key)) out.set(key, { zone_id, zone_label: zone_id ? (zoneLabel.get(zone_id) ?? '?') : '—', month, counts: {}, wateredLitres: 0, total: 0 });
    const r = out.get(key)!;
    r.counts[i.type] = (r.counts[i.type] ?? 0) + 1;
    r.total += 1;
    if (i.type === 'watered' && i.unit === 'L' && i.quantity != null) r.wateredLitres += Number(i.quantity);
  }
  return [...out.values()].sort((a, b) => b.month.localeCompare(a.month) || a.zone_label.localeCompare(b.zone_label));
}

export type FlagKey = 'stressed_twice' | 'not_found_last' | 'not_found_twice' | 'possible_mis_tag';
export const FLAG_KEYS: readonly FlagKey[] = ['stressed_twice', 'not_found_last', 'not_found_twice', 'possible_mis_tag'];

export interface ActiveFlag {
  tree_id: string;
  tag: string;
  zone_label: string;
  flags: FlagKey[];
}

/** Trees with at least one raised flag, with tag and zone, sorted by tag. */
export function activeFlags(flags: readonly TreeFlagsRow[], trees: readonly TreeRow[], zones: readonly ZoneRow[]): ActiveFlag[] {
  const treeById = new Map(trees.map((t) => [t.id, t]));
  const zoneLabel = new Map(zones.map((z) => [z.id, z.label]));
  const out: ActiveFlag[] = [];
  for (const f of flags) {
    const tree = treeById.get(f.tree_id);
    if (!tree) continue;
    const raised = FLAG_KEYS.filter((k) => f[k] === true);
    if (raised.length === 0) continue;
    out.push({ tree_id: f.tree_id, tag: tree.tag, zone_label: tree.zone_id ? (zoneLabel.get(tree.zone_id) ?? '?') : '—', flags: raised });
  }
  return out.sort((a, b) => a.tag.localeCompare(b.tag));
}

export interface TimelineEntry {
  measurement: MeasurementWithGirths;
  date: string;
  team_name: string | null;
  girthBasal: number | null;
  diameterBasal: number | null;
  girthDbh: number | null;
  diameterDbh: number | null;
}

/** One tree's measurements oldest first, with the survey date and the derived diameters. */
export function treeTimeline(treeId: string, measurements: readonly MeasurementWithGirths[], surveys: readonly SurveyRow[]): TimelineEntry[] {
  const byId = new Map(surveys.map((s) => [s.id, s]));
  return measurements
    .filter((m) => m.tree_id === treeId)
    .map((m) => {
      const s = byId.get(m.survey_id);
      return {
        measurement: m,
        date: s?.date ?? m.recorded_at.slice(0, 10),
        team_name: s?.team_name ?? null,
        girthBasal: girthAt(m, 10),
        diameterBasal: diameterAt(m, 10),
        girthDbh: girthAt(m, 130),
        diameterDbh: diameterAt(m, 130),
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date) || a.measurement.recorded_at.localeCompare(b.measurement.recorded_at));
}

/** Project-wide condition counts from the latest measurement per tree. */
export function latestCounts(latest: readonly TreeLatestRow[]) {
  const conditions = latest.map((l) => l.condition).filter((c): c is Condition => c != null);
  const counts = countConditions(conditions);
  return { counts, summary: survivalRate(counts), unmeasured: latest.length - conditions.length, planted: latest.length };
}
